import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Clock, Copy, Download, LoaderCircle, Lock, LockOpen, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { generateRecoveryKey, passphraseProblems } from "@brainlog/shared";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { usePersonalUnlocked } from "./keyring";
import { buildPersonalScope, memberPublicKeys, usePersonalKeyRecord } from "./personal";
import { PersonalKeySetup, PersonalKeyUnlock } from "./PersonalKey";
import { buildScope, decryptNote, disableProjectEncryption, enableProjectEncryption, encryptExistingNote, type NewScope } from "./convert";
import { saveBlob } from "./ops";

const NOTE_REMOVED = [
  "Its title, text, every saved version, comments and its own attached files are encrypted in this browser before they are stored.",
  "BrainLog, its AI assistant and connected AI apps can never read it. Search, evidence links, task creation (“Create tasks from action items”), export from BrainLog and the AI stop working for it.",
  "Unencrypted copies elsewhere are deleted: its links, evidence links quoting it, quotes in tasks created from it, and AI requests and results that used it.",
  "Copies made earlier (manuscript submissions, backups) keep the text they recorded.",
];

const PROJECT_REMOVED = [
  "Every note (title, text, versions, comments) and every uploaded file in this project is encrypted in this browser. New notes and files are encrypted automatically.",
  "BrainLog, its AI assistant and connected AI apps can never read them. Search, evidence links, the AI and recorded submissions stop working.",
  "Still readable by BrainLog (needed to organise it): folder names, note types and tags, research-record fields and states, tasks, the papers’ details in the library, manuscripts’ titles and checklists, dates and member names.",
  "Stored AI requests and results for this project are deleted. Deleted files are erased.",
  "Copies made earlier (manuscript submissions, backups) keep the text they recorded.",
];

type EncryptTarget = { type: "note"; id: string; title: string; workspaceId: string } | { type: "project"; id: string; name: string; kind?: "notebook" | "project" };

/**
 * Encrypt a note or a whole notebook/project. Recommended: with personal keys — the person opens it
 * with their own passphrase and the members get the key too. Alternatively with a passphrase (and
 * recovery key) that has to be shared through another channel.
 */
export function EncryptDialog(props: { open: boolean; onOpenChange(o: boolean): void; target: EncryptTarget; onDone?(): void }) {
  const me = useMe();
  const record = usePersonalKeyRecord();
  const personalUnlocked = usePersonalUnlocked();
  const [method, setMethod] = useState<"personal" | "passphrase">("personal");
  const [shareWithMembers, setShareWithMembers] = useState(true);
  const [step, setStep] = useState<"passphrase" | "recovery" | "working">("passphrase");
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [useRecovery, setUseRecovery] = useState(true);
  const [saved, setSaved] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const recovery = useMemo(() => generateRecoveryKey(), []);
  const problems = passphraseProblems(pass);
  const mismatch = confirm.length > 0 && confirm !== pass;
  const isProject = props.target.type === "project";
  const what = props.target.type === "note" ? "note" : props.target.kind === "notebook" ? "notebook" : "project";
  const name = props.target.type === "note" ? props.target.title : props.target.name;
  const workspaceId = props.target.type === "note" ? props.target.workspaceId : props.target.id;
  const members = useQuery({ queryKey: ["enc-members", workspaceId], queryFn: () => memberPublicKeys(workspaceId), enabled: props.open });
  const others = (members.data ?? []).filter((m) => m.id !== me.data?.id);
  const includeOthers = isProject || shareWithMembers;

  const run = async () => {
    setStep("working");
    setError(null);
    try {
      let scope: NewScope;
      if (method === "personal") {
        const mine = record.data?.publicKey;
        if (!mine || !me.data) throw new Error("Set up and unlock your personal key first.");
        setProgress("Creating the key and sharing it with personal keys…");
        const recipients = [{ id: me.data.id, publicKey: mine }, ...(includeOthers ? others.filter((m) => m.publicKey).map((m) => ({ id: m.id, publicKey: m.publicKey! })) : [])];
        scope = await buildPersonalScope(recipients);
      } else {
        setProgress("Deriving the key from your passphrase…");
        scope = await buildScope(pass, useRecovery ? recovery : null);
      }
      if (props.target.type === "note") await encryptExistingNote(props.target.id, { newScope: scope }, setProgress);
      else await enableProjectEncryption(props.target.id, scope, setProgress);
      toast.success(`The ${what} is now end-to-end encrypted.`);
      props.onDone?.();
      props.onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
      setStep(method === "passphrase" && useRecovery ? "recovery" : "passphrase");
    }
  };

  const memberList = (
    <ul className="mt-1 space-y-0.5 text-xs">
      {others.map((m) => (
        <li key={m.id} className="flex items-center gap-1.5">
          {m.publicKey ? <Check className="size-3 text-primary" aria-hidden /> : <Clock className="size-3 text-muted-foreground" aria-hidden />}
          {m.displayName} — {m.publicKey ? "gets access now" : "can be given access once they set up a personal key"}
        </li>
      ))}
    </ul>
  );

  return (
    <Dialog open={props.open} onOpenChange={(o) => step !== "working" && props.onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="size-4" aria-hidden /> Encrypt this {what}
          </DialogTitle>
          <DialogDescription>“{name}” is encrypted in your browser; the key never reaches BrainLog.</DialogDescription>
        </DialogHeader>

        {step === "passphrase" && (
          <div className="space-y-4">
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {(isProject ? PROJECT_REMOVED : NOTE_REMOVED).map((l) => (
                <li key={l}>{l.replaceAll("this project", `this ${what}`)}</li>
              ))}
            </ul>
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">How it opens</legend>
              <label className={cn("block cursor-pointer rounded-md border p-3 text-sm", method === "personal" && "border-primary bg-accent/50")}>
                <span className="flex items-center gap-2 font-medium">
                  <input type="radio" name="enc-method" checked={method === "personal"} onChange={() => setMethod("personal")} /> With personal keys (recommended)
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  You open it with your own personal passphrase.{" "}
                  {isProject ? `Members of this ${what} get the key automatically — and so does anyone you share it with later.` : "No separate passphrase to remember or pass around."}
                </span>
                {method === "personal" && others.length > 0 && (
                  <>
                    {!isProject && (
                      <span className="mt-2 flex items-start gap-2 text-xs">
                        <Checkbox checked={shareWithMembers} onCheckedChange={(v) => setShareWithMembers(v === true)} className="mt-0.5" aria-label="Also give the other members access" />
                        <span>Also give the other members access</span>
                      </span>
                    )}
                    {includeOthers && memberList}
                  </>
                )}
              </label>
              <label className={cn("block cursor-pointer rounded-md border p-3 text-sm", method === "passphrase" && "border-primary bg-accent/50")}>
                <span className="flex items-center gap-2 font-medium">
                  <input type="radio" name="enc-method" checked={method === "passphrase"} onChange={() => setMethod("passphrase")} /> With a passphrase
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">Everyone who should open it needs the passphrase; you share it through a separate channel.</span>
              </label>
            </fieldset>

            {method === "personal" &&
              (record.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading your personal key…</p>
              ) : !record.data ? (
                <div className="space-y-2 rounded-md border p-3">
                  <p className="text-sm font-medium">First, set up your personal key</p>
                  <PersonalKeySetup compact />
                </div>
              ) : !personalUnlocked ? (
                <div className="space-y-2 rounded-md border p-3">
                  <p className="text-sm font-medium">Unlock your personal key</p>
                  <PersonalKeyUnlock />
                </div>
              ) : null)}

            {method === "passphrase" && (
              <form
                id="enc-pass-form"
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (problems.length || mismatch || !confirm) return;
                  if (useRecovery) setStep("recovery");
                  else void run();
                }}
              >
                <div className="space-y-1.5">
                  <Label htmlFor="enc-pass">Passphrase</Label>
                  <Input id="enc-pass" type="password" autoComplete="new-password" autoFocus value={pass} onChange={(e) => setPass(e.target.value)} aria-describedby="enc-pass-help" />
                  <p id="enc-pass-help" className="text-xs text-muted-foreground">
                    {problems.length ? problems.join(" ") : "Good. Share it only with people who should open this — through a separate channel."}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enc-confirm">Repeat the passphrase</Label>
                  <Input id="enc-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={mismatch} />
                  {mismatch && <p className="text-xs text-destructive">The passphrases do not match.</p>}
                </div>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={useRecovery} onCheckedChange={(v) => setUseRecovery(v === true)} className="mt-0.5" />
                  <span>Also create a recovery key (recommended) — it opens the content if the passphrase is forgotten.</span>
                </label>
              </form>
            )}

            <Alert variant="destructive">
              <TriangleAlert />
              <AlertTitle>There is no reset</AlertTitle>
              <AlertDescription>
                {method === "personal"
                  ? "If everyone who can open it loses their personal passphrase and recovery key, the content cannot be recovered by anyone, not even BrainLog."
                  : `If the passphrase ${useRecovery ? "and the recovery key are" : "is"} lost, the content cannot be recovered by anyone, not even BrainLog.`}
              </AlertDescription>
            </Alert>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => props.onOpenChange(false)}>
                Cancel
              </Button>
              {method === "personal" ? (
                <Button type="button" disabled={!record.data || !personalUnlocked || members.isLoading} onClick={() => void run()}>
                  <Lock /> Encrypt {what}
                </Button>
              ) : (
                <Button type="submit" form="enc-pass-form" disabled={!!problems.length || !confirm || mismatch}>
                  {useRecovery ? "Next" : `Encrypt ${what}`}
                </Button>
              )}
            </DialogFooter>
          </div>
        )}

        {step === "recovery" && (
          <div className="space-y-4">
            <p className="text-sm">Store this recovery key somewhere safe (a password manager or printed). It is shown only now.</p>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
              <code className="flex-1 break-all font-mono text-sm tracking-wide" data-testid="recovery-key">
                {recovery}
              </code>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Copy recovery key"
                onClick={() => navigator.clipboard.writeText(recovery).then(() => toast.success("Recovery key copied"), () => toast.error("Copy failed — select and copy it manually."))}
              >
                <Copy />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Download recovery key"
                onClick={() =>
                  saveBlob(
                    new Blob([`BrainLog recovery key\n${what[0].toUpperCase()}${what.slice(1)}: ${name}\nCreated: ${new Date().toISOString()}\n\n${recovery}\n`], { type: "text/plain" }),
                    "brainlog-recovery-key.txt",
                  )
                }
              >
                <Download />
              </Button>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={saved} onCheckedChange={(v) => setSaved(v === true)} className="mt-0.5" />
              <span>I stored the recovery key somewhere safe.</span>
            </label>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("passphrase")}>
                Back
              </Button>
              <Button disabled={!saved} onClick={() => void run()}>
                <Lock /> Encrypt {what}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "working" && (
          <p className="flex items-center gap-2 py-6 text-sm" role="status" aria-live="polite">
            <LoaderCircle className="size-4 animate-spin" /> {progress || "Working…"}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Turn encryption off for a note or project (requires it to be unlocked). */
export function DecryptDialog(props: { open: boolean; onOpenChange(o: boolean): void; target: { type: "note"; id: string; title: string } | { type: "project"; id: string; name: string; kind?: "notebook" | "project" }; onDone?(): void }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const isProject = props.target.type === "project";
  const what = props.target.type === "note" ? "note" : props.target.kind === "notebook" ? "notebook" : "project";
  const name = props.target.type === "note" ? props.target.title : props.target.name;
  return (
    <Dialog open={props.open} onOpenChange={(o) => !busy && props.onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockOpen className="size-4" aria-hidden /> {isProject ? `Turn off encryption for this ${what}` : "Remove encryption from this note"}
          </DialogTitle>
          <DialogDescription>
            {isProject
              ? `New notes and files in “${name}” will be stored unencrypted, and every encrypted note and file is decrypted in your browser now. Members, BrainLog's features and the AI assistant will be able to read them. The key is deleted afterwards. Notes with their own key keep it.`
              : `“${name}” will be decrypted in your browser and stored unencrypted again. Members, BrainLog's features and the AI assistant will be able to read it. The key is deleted.`}
          </DialogDescription>
        </DialogHeader>
        {busy && (
          <p className="flex items-center gap-2 text-sm" role="status" aria-live="polite">
            <LoaderCircle className="size-4 animate-spin" /> {progress || "Working…"}
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                if (props.target.type === "note") await decryptNote(props.target.id, setProgress);
                else await disableProjectEncryption(props.target.id, setProgress);
                toast.success(isProject ? `Encryption is off for this ${what}.` : "Encryption removed from the note.");
                props.onDone?.();
                props.onOpenChange(false);
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            <Check /> {isProject ? "Decrypt everything" : "Remove encryption"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
